// Confidentialité des salariés (15/10/2026) : réglages de chaque société cliente, texte d'engagement du représentant légal
// (versionné) et règles d'AFFICHAGE du nom. Partagé par le serveur (qui applique tout) et l'interface (qui ne fait qu'afficher).
//
// Principes :
//  - la traçabilité interne reste par identifiant de compte (coches signées, journal, audit) : seul l'AFFICHAGE du nom change ;
//  - valeurs par défaut = les plus protectrices compatibles avec l'usage du terrain (voir docs/DECISIONS.md, 15/10, RGPD salariés) ;
//  - le texte d'engagement n'est JAMAIS modifié sur place : une nouvelle rédaction = une nouvelle version (V2…), qui redemande
//    l'engagement de chaque société.

export type ModeNom = 'complet' | 'prenom_initiale' | 'prenom';

export interface ReglagesSalaries {
  /** Nom des salariés montré aux AUTRES utilisateurs (chacun voit toujours son propre nom complet). */
  nom: ModeNom;
  /** Téléphone professionnel des techniciens visible de leurs coéquipiers ; non = seul l'encadrement le voit. */
  telephoneCoequipiers: boolean;
  /** Photos de chantier ; non = le serveur refuse tout nouvel envoi (les photos existantes restent). */
  photos: boolean;
  /** Déclaration du temps passé ; non = le serveur refuse toute déclaration (l'existant reste, lisible de l'encadrement). */
  temps: boolean;
}

export const REGLAGES_SALARIES_DEFAUT: ReglagesSalaries = { nom: 'prenom_initiale', telephoneCoequipiers: false, photos: true, temps: true };

export const LIBELLES_MODE_NOM: Record<ModeNom, string> = {
  complet: 'Nom complet',
  prenom_initiale: 'Prénom et initiale du nom',
  prenom: 'Prénom seul',
};

/** Exemple affiché à côté de chaque mode (personne fictive). */
export const EXEMPLES_MODE_NOM: Record<ModeNom, string> = { complet: 'Paul Martin', prenom_initiale: 'Paul M.', prenom: 'Paul' };

/** Récapitulatif lisible des réglages (écran d'engagement, historique, note d'information). */
export function recapitulatifReglages(r: ReglagesSalaries): { libelle: string; valeur: string }[] {
  return [
    { libelle: 'Nom des salariés montré aux autres utilisateurs', valeur: LIBELLES_MODE_NOM[r.nom] },
    { libelle: 'Téléphone professionnel des techniciens', valeur: r.telephoneCoequipiers ? 'Visible de leurs coéquipiers' : 'Visible de l’encadrement seulement' },
    { libelle: 'Photos de chantier', valeur: r.photos ? 'Activées (photographier l’ouvrage, jamais les personnes)' : 'Désactivées' },
    { libelle: 'Déclaration du temps passé', valeur: r.temps ? 'Activée (suivi de chantier, pas le décompte légal du temps de travail)' : 'Désactivée' },
  ];
}

/** Forme canonique des réglages engagés : ordre fixe des clés, seules les quatre valeurs (l'empreinte SHA-256 porte sur ce texte). */
export function reglagesCanoniques(r: ReglagesSalaries): string {
  return JSON.stringify({ nom: r.nom, telephoneCoequipiers: r.telephoneCoequipiers, photos: r.photos, temps: r.temps });
}

// ─── Texte d'engagement ─────────────────────────────────────────────────────────────────────────────────────────────
export const QUALITES_SIGNATAIRE = {
  gerant: 'Gérant(e)',
  president: 'Président(e)',
  directeur_general: 'Directeur (directrice) général(e)',
  directeur_general_delegue: 'Directeur (directrice) général(e) délégué(e)',
  delegataire: 'Délégataire (délégation de pouvoir)',
  autre: 'Autre représentant légal',
} as const;
export type QualiteSignataire = keyof typeof QUALITES_SIGNATAIRE;
/** Qualités qui exigent une précision (« délégation donnée par … le … », « co-gérant », « président du directoire »…). */
export const QUALITES_A_PRECISER: QualiteSignataire[] = ['delegataire', 'autre'];

export interface TexteEngagement { version: number; titre: string; preambule: string; declarations: string[]; portee: string }

export const TEXTE_ENGAGEMENT_V1: TexteEngagement = {
  version: 1,
  titre: 'Engagement relatif aux données des salariés enregistrées dans TRAME — version 1',
  preambule: 'La société, représentée par la personne signataire, déclare :',
  declarations: [
    'être responsable du traitement des données personnelles de ses salariés enregistrées dans TRAME (règlement (UE) 2016/679, '
      + 'dit RGPD, articles 4 et 24) ; l’éditeur de TRAME agit pour son compte en qualité de sous-traitant (article 28), dans les '
      + 'conditions du contrat qui les lie ;',
    'avoir choisi elle-même les réglages récapitulés avec cet engagement (affichage du nom, téléphone professionnel, photos de '
      + 'chantier, déclaration du temps passé), au regard de ses besoins et du principe de minimisation des données ;',
    'avoir informé individuellement chaque salarié concerné, avant la mise en service, des données enregistrées, de leurs '
      + 'finalités, de leurs destinataires, de leur durée de conservation et de ses droits (RGPD, article 13 ; Code du travail, '
      + 'article L1222-4) ;',
    'avoir informé et consulté le comité social et économique, lorsqu’il en existe un, sur la mise en œuvre de cet outil '
      + '(Code du travail, article L2312-38) ;',
    'tenir son registre des activités de traitement et y avoir inscrit ce traitement (RGPD, article 30) ;',
    'savoir que le temps passé déclaré dans TRAME est un suivi de chantier et non le décompte légal du temps de travail de ses salariés.',
  ],
  portee: 'La case cochée atteste des choix et des démarches de la société à la date de l’engagement. Toute modification des '
    + 'réglages, ou une nouvelle version de ce texte, demande un nouvel engagement ; les engagements précédents restent conservés.',
};

/** Version 2 (relue le 15/10/2026) : remplace la V1, jamais signée hors démonstration. Copie lisible : docs/ENGAGEMENT-RGPD-V2.md. */
export const TEXTE_ENGAGEMENT_V2: TexteEngagement = {
  version: 2,
  titre: 'Engagement relatif aux données des salariés enregistrées dans TRAME — version 2',
  preambule: 'La société, représentée par la personne signataire, qui déclare avoir le pouvoir de l’engager, déclare et s’engage :',
  declarations: [
    'être responsable du traitement des données personnelles de ses salariés enregistrées dans TRAME (règlement (UE) 2016/679, '
      + 'dit RGPD, articles 4 et 24) ; lorsque l’éditeur de TRAME héberge l’application ou intervient sur l’installation de la '
      + 'société, il agit pour le compte de celle-ci et sur ses seules instructions, en qualité de sous-traitant (article 28), dans '
      + 'les conditions de l’accord de sous-traitance des données (article 28, paragraphe 3) qui les lie ;',
    'avoir choisi elle-même les réglages récapitulés avec cet engagement (affichage du nom, téléphone professionnel, photos de '
      + 'chantier, déclaration du temps passé), comme justifiés par la nature des tâches et proportionnés au but recherché (RGPD, '
      + 'article 5 ; Code du travail, article L1121-1) ;',
    'informer individuellement chaque salarié concerné avant sa première utilisation de TRAME, notamment en lui remettant la note '
      + 'd’information produite par TRAME, complétée, et en conserver la preuve : données enregistrées, finalités, bases légales '
      + '(autres que le consentement), destinataires, durées de conservation, droits et réclamation auprès de la CNIL (RGPD, '
      + 'article 13 ; Code du travail, article L1222-4) ;',
    'lorsqu’elle y est tenue (comité social et économique d’une entreprise d’au moins cinquante salariés), avoir informé et '
      + 'consulté ce comité préalablement à la décision de mettre en œuvre TRAME, outil qui permet de connaître l’activité des '
      + 'salariés (Code du travail, article L2312-38) ;',
    'tenir son registre des activités de traitement, y inscrire ce traitement avec les durées de conservation qu’elle retient, et '
      + 'faire appliquer ces durées (RGPD, articles 5 et 30) ;',
    'savoir que le temps passé déclaré dans TRAME est un suivi de chantier et non le décompte légal du temps de travail : il ne '
      + 'remplace pas les documents de décompte de la durée du travail qui lui incombent (Code du travail, articles L3171-2 et '
      + 'D3171-8) et peut être produit en cas de litige sur les heures de travail (article L3171-4) ;',
    'n’activer l’assistance par intelligence artificielle avec envoi d’extraits de documents ou de photos à un fournisseur établi '
      + 'hors de l’Union européenne qu’après avoir vérifié l’encadrement de cet envoi (RGPD, chapitre V) et en avoir informé ses '
      + 'salariés.',
  ],
  portee: 'La case cochée depuis le compte connecté exprime l’accord de la société, par la personne signataire, sur ces réglages, '
    + 'déclarations et engagements (Code civil, articles 1366 et 1367). Elle atteste des choix et des démarches de la société à la '
    + 'date de l’engagement et ne déplace aucune responsabilité entre la société et l’éditeur, qui restent chacun tenus de leurs '
    + 'obligations propres. Toute modification des réglages, ou une nouvelle version de ce texte, demande un nouvel engagement ; les '
    + 'engagements précédents restent conservés.',
};

export const TEXTE_ENGAGEMENT_EN_VIGUEUR = TEXTE_ENGAGEMENT_V2;

/** Texte exact sur lequel porte l'empreinte SHA-256 (titre, préambule, déclarations numérotées, portée ; lignes séparées par \n). */
export function texteEngagementCanonique(t: TexteEngagement): string {
  return [t.titre, t.preambule, ...t.declarations.map((x, i) => `${i + 1}. ${x}`), t.portee].join('\n');
}

// ─── Affichage du nom ───────────────────────────────────────────────────────────────────────────────────────────────
const PARTICULES = new Set(['de', 'du', 'des', 'd', 'le', 'la', 'les', 'van', 'von', 'der', 'den', 'di', 'da', 'del', 'dos', 'ben', 'el']);
const NOM_MAX = 200;

/** Majuscule de « nom » sur au moins deux lettres (« MARTIN » dans « MARTIN Paul »). */
const enCapitales = (mot: string) => mot.length >= 2 && /\p{L}/u.test(mot) && mot === mot.toLocaleUpperCase('fr') && mot !== mot.toLocaleLowerCase('fr');

/**
 * Découpe « Prénom Nom (précision) ». Le nom écrit en CAPITALES est reconnu où qu'il soit (« MARTIN Paul », « Paul MARTIN ») ;
 * sinon le premier mot est le prénom. Une précision finale entre parenthèses (« (technicien fictif) ») est gardée à part.
 * Un seul mot : c'est le prénom (affiché tel quel dans tous les modes).
 */
export function decouperNom(brut: string): { prenom: string; nom: string; precision: string } {
  let s = String(brut ?? '').slice(0, NOM_MAX).trim().replace(/\s+/g, ' ');
  let precision = '';
  const p = /\s*(\([^()]*\))$/.exec(s);
  if (p && p.index > 0) { precision = p[1]; s = s.slice(0, p.index).trim(); }
  const mots = s.split(' ').filter(Boolean);
  if (mots.length <= 1) return { prenom: mots[0] ?? '', nom: '', precision };
  const capitales = mots.map(enCapitales);
  if (capitales.some(Boolean) && !capitales.every(Boolean)) {
    return { prenom: mots.filter((_, i) => !capitales[i]).join(' '), nom: mots.filter((_, i) => capitales[i]).join(' '), precision };
  }
  return { prenom: mots[0], nom: mots.slice(1).join(' '), precision };
}

/** Mot du nom qui porte l'initiale : le premier qui n'est pas une particule (« de La Fontaine » → « Fontaine »). */
function motPrincipal(nom: string) {
  const mots = nom.split(/[\s']+/).filter(Boolean);
  return mots.find((m) => !PARTICULES.has(m.toLocaleLowerCase('fr'))) ?? mots[mots.length - 1] ?? '';
}

const capitaliser = (m: string) => (m ? m.charAt(0).toLocaleUpperCase('fr') + m.slice(1).toLocaleLowerCase('fr') : m);

/** Prénom suivi des n premières lettres du nom (n = 1 : « Paul M. » ; plus pour départager deux homonymes). */
function prenomEtLettres(n: { prenom: string; nom: string }, lettres: number) {
  const m = motPrincipal(n.nom);
  if (!m) return n.prenom;
  const debut = Array.from(m).slice(0, lettres).join('');
  const complet = debut.length >= Array.from(m).length;
  return `${n.prenom} ${capitaliser(debut)}${complet ? '' : '.'}`;
}

const avecPrecision = (texte: string, precision: string) => (precision ? `${texte} ${precision}` : texte);

/**
 * Noms affichés d'un ensemble de comptes (toute l'organisation, pour un résultat stable d'un écran à l'autre).
 *  - « complet » : le nom tel qu'enregistré ;
 *  - « prénom et initiale » : « Paul M. » ; deux comptes qui donneraient le même texte reçoivent assez de lettres pour se distinguer
 *    (« Paul Ma. », « Paul Mo. »), jusqu'au nom entier ;
 *  - « prénom seul » : « Paul » ; deux « Paul » reçoivent l'initiale (puis les lettres) nécessaire pour se distinguer — sans quoi
 *    l'équipe ne saurait plus qui a fait quoi.
 * Deux comptes au nom complet identique restent identiques (comme en mode « complet »).
 */
export function nomsAffiches(comptes: { id: string; nom: string }[], mode: ModeNom): Map<string, string> {
  const res = new Map<string, string>();
  if (mode === 'complet') { for (const c of comptes) res.set(c.id, String(c.nom ?? '').slice(0, NOM_MAX).trim()); return res; }
  const parts = comptes.map((c) => ({ id: c.id, n: decouperNom(c.nom), complet: String(c.nom ?? '').slice(0, NOM_MAX).trim() }));
  const niveau = new Map(parts.map((p) => [p.id, mode === 'prenom' ? 0 : 1]));
  const texte = (p: (typeof parts)[number]) => {
    const l = niveau.get(p.id)!;
    return avecPrecision(l === 0 || !p.n.nom ? p.n.prenom : prenomEtLettres(p.n, l), p.n.precision);
  };
  for (let tour = 0; tour < 60; tour++) {
    const groupes = new Map<string, typeof parts>();
    for (const p of parts) { const t = texte(p).toLocaleLowerCase('fr'); groupes.set(t, [...(groupes.get(t) ?? []), p]); }
    let change = false;
    for (const g of groupes.values()) {
      if (g.length < 2) continue;
      // Homonymes complets : rien ne les distingue, inutile d'allonger.
      if (new Set(g.map((p) => p.complet.toLocaleLowerCase('fr'))).size === 1) continue;
      for (const p of g) {
        const max = Array.from(motPrincipal(p.n.nom)).length;
        const l = niveau.get(p.id)!;
        if (p.n.nom && l < max) { niveau.set(p.id, l + 1); change = true; }
      }
    }
    if (!change) break;
  }
  for (const p of parts) res.set(p.id, texte(p) || p.complet);
  return res;
}
